import React, { useState, useEffect, useMemo } from 'react';
import api from '../../services/api';

const Variables = () => {
    const [variables, setVariables] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [bulkPrice, setBulkPrice] = useState('0.50');
    const [globalToppingPrice, setGlobalToppingPrice] = useState('0.50');
    const [updatingGlobal, setUpdatingGlobal] = useState(false);
    
    const [formData, setFormData] = useState({
        id: null,
        name: '',
        is_active: true,
        products: [],
        options: []
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [varRes, prodRes] = await Promise.all([
                api.get('/api/menu/variables/'),
                api.get('/api/menu/products/')
            ]);
            const varData = varRes.data.results || varRes.data;
            const prodData = prodRes.data.results || prodRes.data;
            setVariables(Array.isArray(varData) ? varData : []);
            setProducts(Array.isArray(prodData) ? prodData : []);
            setError(null);
        } catch (err) {
            console.error('Error fetching data:', err);
            setError('Error al cargar los datos');
        } finally {
            setLoading(false);
        }
    };

    const handleApplyGlobalPrice = async () => {
        const p = parseFloat(globalToppingPrice);
        if (isNaN(p) || p < 0) {
            alert('Por favor ingrese un valor de precio válido (ej: 0.50)');
            return;
        }
        try {
            setUpdatingGlobal(true);
            await api.post('/api/menu/toppings/bulk-price/', { price: p });
            alert(`✅ ¡Precio de adicionales actualizado a $${p.toFixed(2)} en todo el sistema!`);
            fetchData();
        } catch (err) {
            console.error('Error updating bulk topping price:', err);
            alert('Error al actualizar el precio general de toppings');
        } finally {
            setUpdatingGlobal(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        });
    };

    const addOptionRow = () => {
        setFormData({
            ...formData,
            options: [
                ...formData.options, 
                { id: null, name: '', extra_price: bulkPrice !== '' ? (parseFloat(bulkPrice) || 0) : 0.50, is_active: true, is_deleted: false }
            ]
        });
    };

    const handleOptionChange = (index, field, value) => {
        const newOptions = [...formData.options];
        newOptions[index][field] = value;
        setFormData({ ...formData, options: newOptions });
    };

    const removeOptionRow = (index) => {
        const newOptions = [...formData.options];
        if (newOptions[index].id) {
            newOptions[index].is_deleted = true;
        } else {
            newOptions.splice(index, 1);
        }
        setFormData({ ...formData, options: newOptions });
    };

    const applyBulkPrice = () => {
        const price = parseFloat(bulkPrice) || 0;
        const newOptions = formData.options.map(opt => ({
            ...opt,
            extra_price: price
        }));
        setFormData({ ...formData, options: newOptions });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setSaving(true);
            let variableId = formData.id;
            
            // 1. Guardar o Actualizar la Variable
            const variablePayload = {
                name: formData.name.trim(),
                is_active: formData.is_active,
                products: formData.products
            };
            
            if (variableId) {
                await api.put(`/api/menu/variables/${variableId}/`, variablePayload);
            } else {
                const res = await api.post('/api/menu/variables/', variablePayload);
                variableId = res.data.id;
            }

            // 2. Procesar las opciones con su precio extra
            for (const opt of formData.options) {
                if (opt.is_deleted && opt.id) {
                    await api.delete(`/api/menu/variable-options/${opt.id}/`);
                } else if (!opt.is_deleted && opt.name.trim()) {
                    const optPayload = {
                        variable: variableId,
                        name: opt.name.trim(),
                        extra_price: parseFloat(opt.extra_price) || 0,
                        is_active: opt.is_active !== undefined ? opt.is_active : true
                    };
                    if (opt.id) {
                        await api.put(`/api/menu/variable-options/${opt.id}/`, optPayload);
                    } else {
                        await api.post('/api/menu/variable-options/', optPayload);
                    }
                }
            }

            setShowModal(false);
            fetchData();
            resetForm();
        } catch (err) {
            console.error('Error saving variable:', err);
            alert('Error al guardar la variable');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('¿Está seguro de eliminar esta variable y todas sus opciones?')) {
            try {
                await api.delete(`/api/menu/variables/${id}/`);
                fetchData();
            } catch (err) {
                console.error('Error deleting variable:', err);
                alert('Error al eliminar la variable');
            }
        }
    };

    const editVariable = (variable) => {
        setFormData({
            id: variable.id,
            name: variable.name,
            is_active: variable.is_active,
            products: variable.products || [],
            options: variable.options ? variable.options.map(o => ({ 
                ...o, 
                extra_price: parseFloat(o.extra_price) || 0,
                is_deleted: false 
            })) : []
        });
        setBulkPrice('');
        setShowModal(true);
    };

    const resetForm = () => {
        setFormData({
            id: null,
            name: '',
            is_active: true,
            products: [],
            options: []
        });
        setBulkPrice('');
    };

    const filteredVariables = useMemo(() => {
        if (!searchTerm) return variables;
        const s = searchTerm.toLowerCase();
        return variables.filter(v => 
            v.name?.toLowerCase().includes(s) ||
            v.options?.some(o => o.name?.toLowerCase().includes(s))
        );
    }, [variables, searchTerm]);

    if (loading) return (
        <div style={{ padding: '3rem', textAlign: 'center', color: '#6b7280' }}>
            <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}></div>
            <p style={{ marginTop: '1rem', fontWeight: '500' }}>Cargando variables y toppings...</p>
        </div>
    );

    if (error) return (
        <div style={{ padding: '2rem', maxWidth: '800px', margin: '0 auto' }}>
            <div style={{ backgroundColor: '#fee2e2', border: '1px solid #ef4444', borderRadius: '8px', padding: '1rem', color: '#991b1b' }}>
                {error}
            </div>
        </div>
    );

    return (
        <div style={{ padding: '2rem', maxWidth: '1100px', margin: '0 auto' }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
                <div>
                    <h2 style={{ margin: 0, color: 'var(--primary-color)', fontSize: '1.75rem', fontWeight: 'bold' }}>
                        ⚙️ Variables y Toppings
                    </h2>
                    <p style={{ margin: '0.25rem 0 0 0', color: '#6b7280', fontSize: '0.9rem' }}>
                        Configura grupos de opciones (Toppings, Sabores, Salsas) con sus precios extras para el Punto de Venta.
                    </p>
                </div>
                <button 
                    onClick={() => { resetForm(); setShowModal(true); }}
                    style={{
                        padding: '0.75rem 1.5rem',
                        backgroundColor: 'var(--primary-color)',
                        color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer',
                        fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '0.5rem',
                        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
                    }}
                >
                    <i className="bi bi-plus-circle-fill"></i> Nueva Variable / Topping
                </button>
            </div>

            {/* Info Banner */}
            <div style={{
                backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '10px',
                padding: '1rem 1.25rem', marginBottom: '1.25rem', display: 'flex', gap: '0.75rem', alignItems: 'center'
            }}>
                <span style={{ fontSize: '1.5rem' }}>💡</span>
                <div style={{ fontSize: '0.875rem', color: '#1e40af' }}>
                    <strong>¿Cómo funciona el cobro en caja?</strong> Si una variable se llama <em>"Toppings"</em> o <em>"Adicionales"</em>, los primeros toppings seleccionados por el cliente (según el límite configurado en el Menú) son gratis. Cualquier adicional suma su <strong>Precio Extra</strong> directamente al total de la orden, se refleja en el ticket, entra a la caja y se reporta en las ventas.
                </div>
            </div>

            {/* APARTADO DE CONFIGURACIÓN DEL VALOR ADICIONAL */}
            <div style={{
                backgroundColor: '#f0fdf4',
                border: '1px solid #bbf7d0',
                borderRadius: '12px',
                padding: '1.1rem 1.5rem',
                marginBottom: '1.5rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '1rem',
                boxShadow: '0 2px 4px rgba(0,0,0,0.03)'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <div style={{ fontSize: '2rem' }}>💰</div>
                    <div>
                        <h4 style={{ margin: 0, color: '#166534', fontSize: '1.05rem', fontWeight: 'bold' }}>
                            Valor por Topping Adicional
                        </h4>
                        <p style={{ margin: '0.2rem 0 0 0', color: '#15803d', fontSize: '0.85rem' }}>
                            Establece el precio base cobrado por cada topping extra en el Punto de Venta.
                        </p>
                    </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', backgroundColor: '#fff', border: '2px solid #86efac', borderRadius: '8px', padding: '0.35rem 0.75rem' }}>
                        <span style={{ fontWeight: 'bold', color: '#166534', fontSize: '1.1rem', marginRight: '0.25rem' }}>$</span>
                        <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={globalToppingPrice}
                            onChange={(e) => setGlobalToppingPrice(e.target.value)}
                            style={{
                                width: '75px',
                                border: 'none',
                                outline: 'none',
                                fontSize: '1.1rem',
                                fontWeight: 'bold',
                                color: '#166534'
                            }}
                            placeholder="0.50"
                        />
                    </div>
                    <button
                        type="button"
                        onClick={handleApplyGlobalPrice}
                        disabled={updatingGlobal}
                        style={{
                            padding: '0.55rem 1.25rem',
                            backgroundColor: '#16a34a',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '8px',
                            fontWeight: 'bold',
                            fontSize: '0.9rem',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.4rem',
                            boxShadow: '0 2px 4px rgba(22, 163, 74, 0.2)'
                        }}
                    >
                        <i className="bi bi-check-all"></i> {updatingGlobal ? 'Guardando...' : 'Aplicar a Todos'}
                    </button>
                </div>
            </div>

            {/* Search Bar */}
            <div style={{ marginBottom: '1.25rem' }}>
                <input 
                    type="text" 
                    placeholder="🔍 Buscar variable o topping por nombre..." 
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                        width: '100%', padding: '0.75rem 1rem', borderRadius: '8px',
                        border: '1px solid #d1d5db', fontSize: '0.95rem', outline: 'none'
                    }}
                />
            </div>

            {/* Variables Table */}
            <div style={{ backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', overflow: 'hidden', border: '1px solid #e5e7eb' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                            <th style={{ padding: '1rem', textAlign: 'left', color: '#374151', fontWeight: '600' }}>Variable</th>
                            <th style={{ padding: '1rem', textAlign: 'left', color: '#374151', fontWeight: '600' }}>Opciones y Precios Extras</th>
                            <th style={{ padding: '1rem', textAlign: 'center', color: '#374151', fontWeight: '600' }}>Productos</th>
                            <th style={{ padding: '1rem', textAlign: 'center', color: '#374151', fontWeight: '600' }}>Estado</th>
                            <th style={{ padding: '1rem', textAlign: 'right', color: '#374151', fontWeight: '600' }}>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredVariables.map(v => {
                            const isToppingGroup = v.name?.toLowerCase().includes('topping') || v.name?.toLowerCase().includes('adicional');
                            return (
                                <tr key={v.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                    <td style={{ padding: '1rem', fontWeight: '600', color: '#111827' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                            <span>{isToppingGroup ? '🍨' : '✨'}</span>
                                            <span>{v.name}</span>
                                            {isToppingGroup && (
                                                <span style={{ fontSize: '0.7rem', padding: '0.15rem 0.45rem', backgroundColor: '#fef3c7', color: '#92400e', borderRadius: '999px', fontWeight: 'bold' }}>
                                                    Toppings
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td style={{ padding: '1rem' }}>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                                            {v.options && v.options.length > 0 ? (
                                                v.options.map(opt => {
                                                    const price = parseFloat(opt.extra_price) || 0;
                                                    return (
                                                        <span 
                                                            key={opt.id} 
                                                            style={{
                                                                display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                                                                padding: '0.25rem 0.6rem', borderRadius: '8px', fontSize: '0.8rem',
                                                                backgroundColor: opt.is_active ? '#f3f4f6' : '#fee2e2',
                                                                color: opt.is_active ? '#374151' : '#991b1b',
                                                                border: '1px solid #e5e7eb'
                                                            }}
                                                        >
                                                            <span>{opt.name}</span>
                                                            {price > 0 ? (
                                                                <strong style={{ color: '#059669', fontSize: '0.75rem' }}>
                                                                    (+${price.toFixed(2)})
                                                                </strong>
                                                            ) : (
                                                                <span style={{ color: '#9ca3af', fontSize: '0.75rem' }}>($0.00)</span>
                                                            )}
                                                        </span>
                                                    );
                                                })
                                            ) : (
                                                <span style={{ color: '#9ca3af', fontStyle: 'italic', fontSize: '0.85rem' }}>Sin opciones</span>
                                            )}
                                        </div>
                                    </td>
                                    <td style={{ padding: '1rem', textAlign: 'center', fontSize: '0.85rem', color: '#4b5563' }}>
                                        <span style={{ padding: '0.25rem 0.5rem', backgroundColor: '#e0e7ff', color: '#3730a3', borderRadius: '6px', fontWeight: 'bold' }}>
                                            {v.products?.length || 0} prod.
                                        </span>
                                    </td>
                                    <td style={{ padding: '1rem', textAlign: 'center' }}>
                                        <span style={{ 
                                            padding: '0.25rem 0.75rem', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: 'bold',
                                            backgroundColor: v.is_active ? '#d1fae5' : '#fee2e2',
                                            color: v.is_active ? '#065f46' : '#991b1b'
                                        }}>
                                            {v.is_active ? 'Activa' : 'Inactiva'}
                                        </span>
                                    </td>
                                    <td style={{ padding: '1rem', textAlign: 'right' }}>
                                        <button 
                                            onClick={() => editVariable(v)} 
                                            title="Editar variable"
                                            style={{ background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', marginRight: '0.75rem', fontSize: '1.15rem' }}
                                        >
                                            <i className="bi bi-pencil-square"></i>
                                        </button>
                                        <button 
                                            onClick={() => handleDelete(v.id)} 
                                            title="Eliminar variable"
                                            style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '1.15rem' }}
                                        >
                                            <i className="bi bi-trash"></i>
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                        {filteredVariables.length === 0 && (
                            <tr>
                                <td colSpan="5" style={{ padding: '3rem', textAlign: 'center', color: '#6b7280' }}>
                                    No se encontraron variables. Haz clic en <strong>"Nueva Variable / Topping"</strong> para crear una.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Modal de Creación / Edición */}
            {showModal && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem'
                }}>
                    <div style={{
                        backgroundColor: '#fff', borderRadius: '16px', padding: '2rem',
                        width: '100%', maxWidth: '680px', maxHeight: '90vh', overflowY: 'auto',
                        boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', borderBottom: '1px solid #f3f4f6', paddingBottom: '0.75rem' }}>
                            <h3 style={{ margin: 0, color: '#111827', fontSize: '1.25rem', fontWeight: 'bold' }}>
                                {formData.id ? '✏️ Editar Variable / Topping' : '➕ Nueva Variable / Topping'}
                            </h3>
                            <button 
                                type="button"
                                onClick={() => setShowModal(false)}
                                style={{ background: 'none', border: 'none', fontSize: '1.5rem', color: '#9ca3af', cursor: 'pointer' }}
                            >
                                &times;
                            </button>
                        </div>

                        <form onSubmit={handleSubmit}>
                            {/* Nombre y Estado */}
                            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem', marginBottom: '1.25rem' }}>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '0.35rem', color: '#374151', fontWeight: '600', fontSize: '0.9rem' }}>
                                        Nombre de la Variable
                                    </label>
                                    <input 
                                        type="text" 
                                        name="name" 
                                        placeholder="Ej. Toppings, Salsas, Sabor de Helado"
                                        value={formData.name} 
                                        onChange={handleInputChange} 
                                        required
                                        style={{ width: '100%', padding: '0.65rem 0.85rem', borderRadius: '8px', border: '1px solid #d1d5db', outline: 'none', fontSize: '0.95rem' }}
                                    />
                                    <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                        Usa <strong>"Toppings"</strong> si deseas selección múltiple con límite incluido.
                                    </span>
                                </div>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '0.35rem', color: '#374151', fontWeight: '600', fontSize: '0.9rem' }}>
                                        Estado
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', height: '42px', cursor: 'pointer' }}>
                                        <input 
                                            type="checkbox" 
                                            name="is_active" 
                                            checked={formData.is_active} 
                                            onChange={handleInputChange}
                                            style={{ marginRight: '0.5rem', width: '1.2rem', height: '1.2rem', accentColor: 'var(--primary-color)' }}
                                        />
                                        <span style={{ color: '#374151', fontWeight: '500', fontSize: '0.9rem' }}>Variable Activa</span>
                                    </label>
                                </div>
                            </div>

                            {/* Opciones con Precios Extras */}
                            <div style={{ marginBottom: '1.5rem', border: '1px solid #e5e7eb', padding: '1.25rem', borderRadius: '10px', backgroundColor: '#fcfcfc' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                                    <div>
                                        <label style={{ color: '#111827', fontWeight: 'bold', fontSize: '0.95rem' }}>
                                            Opciones de la Variable & Precios Extras
                                        </label>
                                        <p style={{ margin: 0, fontSize: '0.78rem', color: '#6b7280' }}>
                                            Define cada opción y el valor a cobrar si se escoge como adicional.
                                        </p>
                                    </div>
                                    <button 
                                        type="button" 
                                        onClick={addOptionRow} 
                                        style={{
                                            background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '6px',
                                            padding: '0.4rem 0.85rem', cursor: 'pointer', fontWeight: '600', fontSize: '0.85rem'
                                        }}
                                    >
                                        + Añadir Opción
                                    </button>
                                </div>

                                {/* Bulk Price Helper */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', backgroundColor: '#f3f4f6', padding: '0.5rem 0.75rem', borderRadius: '8px', marginBottom: '0.85rem' }}>
                                    <span style={{ fontSize: '0.8rem', color: '#4b5563', fontWeight: '500' }}>⚡ Precio rápido para todas:</span>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <span style={{ fontSize: '0.85rem', marginRight: '0.25rem', color: '#6b7280' }}>$</span>
                                        <input 
                                            type="number" 
                                            step="0.01" 
                                            placeholder="0.50" 
                                            value={bulkPrice} 
                                            onChange={(e) => setBulkPrice(e.target.value)}
                                            style={{ width: '80px', padding: '0.3rem 0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem' }}
                                        />
                                    </div>
                                    <button 
                                        type="button" 
                                        onClick={applyBulkPrice}
                                        style={{ background: '#4b5563', color: '#fff', border: 'none', borderRadius: '4px', padding: '0.3rem 0.65rem', fontSize: '0.8rem', cursor: 'pointer' }}
                                    >
                                        Aplicar a todas
                                    </button>
                                </div>

                                {/* Table Header for Options */}
                                {formData.options.filter(o => !o.is_deleted).length > 0 && (
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px 60px 40px', gap: '0.5rem', marginBottom: '0.4rem', padding: '0 0.25rem', fontSize: '0.78rem', fontWeight: 'bold', color: '#6b7280' }}>
                                        <span>NOMBRE DE LA OPCIÓN</span>
                                        <span>PRECIO EXTRA ($)</span>
                                        <span style={{ textAlign: 'center' }}>ACTIVO</span>
                                        <span></span>
                                    </div>
                                )}

                                {/* Option Rows */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', maxHeight: '220px', overflowY: 'auto' }}>
                                    {formData.options.map((opt, index) => {
                                        if (opt.is_deleted) return null;
                                        return (
                                            <div key={index} style={{ display: 'grid', gridTemplateColumns: '1fr 140px 60px 40px', gap: '0.5rem', alignItems: 'center' }}>
                                                <input 
                                                    type="text" 
                                                    placeholder="Ej. Oreo, Nutella, Vainilla..." 
                                                    value={opt.name} 
                                                    onChange={(e) => handleOptionChange(index, 'name', e.target.value)} 
                                                    required 
                                                    style={{ padding: '0.45rem 0.65rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9rem' }} 
                                                />
                                                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                                    <span style={{ position: 'absolute', left: '0.6rem', color: '#6b7280', fontSize: '0.85rem', fontWeight: 'bold' }}>$</span>
                                                    <input 
                                                        type="number" 
                                                        step="0.01" 
                                                        placeholder="0.00" 
                                                        value={opt.extra_price} 
                                                        onChange={(e) => handleOptionChange(index, 'extra_price', e.target.value)} 
                                                        style={{ width: '100%', padding: '0.45rem 0.65rem 0.45rem 1.4rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9rem' }} 
                                                    />
                                                </div>
                                                <div style={{ display: 'flex', justifyContent: 'center' }}>
                                                    <input 
                                                        type="checkbox" 
                                                        checked={opt.is_active !== false} 
                                                        onChange={(e) => handleOptionChange(index, 'is_active', e.target.checked)}
                                                        title="Opción activa"
                                                        style={{ width: '1.1rem', height: '1.1rem', accentColor: 'var(--primary-color)', cursor: 'pointer' }}
                                                    />
                                                </div>
                                                <div style={{ display: 'flex', justifyContent: 'center' }}>
                                                    <button 
                                                        type="button" 
                                                        onClick={() => removeOptionRow(index)} 
                                                        title="Eliminar opción"
                                                        style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '1.15rem' }}
                                                    >
                                                        <i className="bi bi-trash-fill"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                    {formData.options.filter(o => !o.is_deleted).length === 0 && (
                                        <div style={{ textAlign: 'center', padding: '1.5rem', color: '#9ca3af', fontSize: '0.875rem' }}>
                                            No has agregado ninguna opción todavía. Haz clic en <strong>"+ Añadir Opción"</strong> arriba.
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Productos Aplicables */}
                            <div style={{ marginBottom: '1.5rem' }}>
                                <label style={{ display: 'block', marginBottom: '0.35rem', color: '#374151', fontWeight: '600', fontSize: '0.9rem' }}>
                                    Aplica a los productos del Menú:
                                </label>
                                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                                    <select 
                                        id="productSelect"
                                        style={{ flex: 1, padding: '0.55rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9rem' }}
                                        defaultValue=""
                                    >
                                        <option value="" disabled>Seleccione un producto para asignarle esta variable...</option>
                                        {products.filter(p => !formData.products.includes(p.id)).map(p => (
                                            <option key={p.id} value={p.id}>{p.name} (${parseFloat(p.price).toFixed(2)})</option>
                                        ))}
                                    </select>
                                    <button 
                                        type="button" 
                                        onClick={() => {
                                            const selectEl = document.getElementById('productSelect');
                                            if (selectEl.value) {
                                                const prodId = selectEl.value;
                                                if (!formData.products.includes(prodId)) {
                                                    setFormData({...formData, products: [...formData.products, prodId]});
                                                    selectEl.value = "";
                                                }
                                            }
                                        }}
                                        style={{ background: 'var(--primary-color)', color: '#fff', border: 'none', borderRadius: '6px', padding: '0 1rem', cursor: 'pointer', fontWeight: '600', fontSize: '0.85rem' }}
                                    >
                                        Asignar
                                    </button>
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', maxHeight: '130px', overflowY: 'auto', padding: '0.5rem', border: '1px solid #e5e7eb', borderRadius: '8px', minHeight: '44px', backgroundColor: '#f9fafb' }}>
                                    {formData.products.length === 0 && (
                                        <span style={{ color: '#9ca3af', fontSize: '0.825rem', fontStyle: 'italic' }}>
                                            Ningún producto seleccionado. (Esta variable no aparecerá hasta que se asigne a al menos un producto).
                                        </span>
                                    )}
                                    {formData.products.map(prodId => {
                                        const p = products.find(prod => prod.id === prodId);
                                        if (!p) return null;
                                        return (
                                            <div key={prodId} style={{ display: 'flex', alignItems: 'center', backgroundColor: '#e0e7ff', color: '#3730a3', padding: '0.2rem 0.6rem', borderRadius: '999px', fontSize: '0.8rem', fontWeight: '600' }}>
                                                <span>{p.name}</span>
                                                <button 
                                                    type="button" 
                                                    onClick={() => {
                                                        setFormData({...formData, products: formData.products.filter(id => id !== prodId)});
                                                    }} 
                                                    style={{ background: 'none', border: 'none', color: '#ef4444', marginLeft: '0.35rem', cursor: 'pointer', fontSize: '0.9rem', display: 'flex', alignItems: 'center' }}
                                                >
                                                    &times;
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Buttons */}
                            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', borderTop: '1px solid #f3f4f6', paddingTop: '1rem' }}>
                                <button 
                                    type="button" 
                                    onClick={() => setShowModal(false)} 
                                    style={{ padding: '0.65rem 1.25rem', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}
                                >
                                    Cancelar
                                </button>
                                <button 
                                    type="submit" 
                                    disabled={saving}
                                    style={{ padding: '0.65rem 1.5rem', backgroundColor: 'var(--primary-color)', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}
                                >
                                    {saving ? 'Guardando...' : (formData.id ? 'Guardar Cambios' : 'Crear Variable')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Variables;
